export const PROPERTY_MATCH_OPTIONS = {
  /** User liked me option (bit 0) */
  LIKED_ME: 0,
  /** Mutual interest option (bit 1) */
  MUTUAL_INTEREST: 1,
  /** Best match option (bit 2) */
  BEST_MATCH: 2,
} as const;

// Type representing one of the PROPERTY_MATCH_OPTIONS values (0, 1, or 2)
export type TPropertyMatchOption = (typeof PROPERTY_MATCH_OPTIONS)[keyof typeof PROPERTY_MATCH_OPTIONS];

/**
 * Checks whether a specific match option bit is active in a bitmask value.
 *
 * @param value - The numeric bitmask representing all selected options.
 * @param optionBit - The match option to check (one of PROPERTY_MATCH_OPTIONS values).
 * @returns `true` if the option is active, otherwise `false`.
 *
 * @example
 * ```ts
 * const value = 5; // binary 101
 * isPropertyMatchOptionActive(value, PROPERTY_MATCH_OPTIONS.LIKED_ME); // true
 * isPropertyMatchOptionActive(value, PROPERTY_MATCH_OPTIONS.MUTUAL_INTEREST); // false
 * isPropertyMatchOptionActive(value, PROPERTY_MATCH_OPTIONS.BEST_MATCH); // true
 * ```
 */
export const isPropertyMatchOptionActive = (value: number, optionBit: TPropertyMatchOption) => {
  return (value & (1 << optionBit)) !== 0;
};

/**
 * Toggles a specific match option bit in a bitmask value.
 *
 * @param value - The current bitmask value.
 * @param optionBit - The match option to toggle (one of PROPERTY_MATCH_OPTIONS values).
 * @returns A new bitmask with the specified option toggled.
 */
export const togglePropertyMatchOption = (value: number, optionBit: TPropertyMatchOption) => {
  return value ^ (1 << optionBit);
};

/**
 * Clears (sets to 0) a specific match option bit in a bitmask value.
 *
 * @param value - The current bitmask value.
 * @param optionBit - The match option to clear (one of PROPERTY_MATCH_OPTIONS values).
 * @returns A new bitmask with the specified option cleared.
 */
export const removePropertyMatchOption = (value: number, optionBit: TPropertyMatchOption) => {
  return value & ~(1 << optionBit);
};
